import {isDevelopment} from "./utils/isDevelopment";
import {SOCKET_MESSAGE_TYPES} from "./types/socketMessageProps";
import {generateRandomString} from "./utils/generateRandomString";
import {setEncryptedPayload} from "./utils/crypto";

const LOCAL_SOCKET_URL = 'ws://localhost:3001';
const PROD_SOCKET_URL = 'wss://vuelve-server.fly.dev';

export class SocketServiceV2 {
    private socket: WebSocket | null = null;
    private retryCount: number = 0;
    private readonly reconnectInterval: number;
    private readonly maxRetries: number;
    private userId;
    private socketUrl;
    private ping = false;
    callback = (event: any) => null;
    private connected = false;
    private socketIdentifier = '';
    private heartbeatTimer: any = null;
    private pingCheckTimer: any = null;
    private focusHandler: () => void;

    onConnected;

    // ✅ FIX: extra handlers (optional but helpful)
    private visibilityHandler: () => void;
    private onlineHandler: () => void;
    private offlineHandler: () => void;

    // ✅ FIX: simple exponential backoff with jitter
    private baseBackoff = 1000;         // 1s
    private maxBackoff = 30000;         // 30s

    // ✅ FIX: optional hooks for delta sync (no behavior change if you don’t pass them)
    private getCursor?: () => string;
    private syncSince?: (cursor: string) => Promise<void>;

    constructor(
        userId: string,
        reconnectInterval = 5000,
        maxRetries = 10,
        callback = (event: any) => null,
        onConnected: () => void = () => null,
        opts?: {
            getCursor?: () => string;               // e.g. () => localStorage.getItem('cursor:global') || ''
            syncSince?: (cursor: string) => Promise<void>; // fetch missed msgs since cursor
            baseBackoffMs?: number;
            maxBackoffMs?: number;
        }
    ) {
        this.userId = userId;
        this.onConnected = onConnected;
        this.retryCount = 0;
        this.reconnectInterval = reconnectInterval;
        this.maxRetries = maxRetries;
        this.connected = false;
        this.callback = callback;
        this.socketIdentifier = setEncryptedPayload(`${userId}|${generateRandomString()}`);
        this.socketUrl = isDevelopment() ? LOCAL_SOCKET_URL : PROD_SOCKET_URL;

        this.getCursor = opts?.getCursor;
        this.syncSince = opts?.syncSince;
        if (opts?.baseBackoffMs) this.baseBackoff = opts.baseBackoffMs;
        if (opts?.maxBackoffMs) this.maxBackoff = opts.maxBackoffMs;

        this.connect();
        // isDevelopment() && console.log("in constructor");

        this.focusHandler = this.onFocus(this);
        window.addEventListener("focus", this.focusHandler);

        this.visibilityHandler = () => {
            if (document.visibilityState === "visible") {
                // try to reconnect quickly & sync possible gap
                if (!this.connected) this.retryConnection(true);
                this.safeSync(); // won’t run if you didn’t provide syncSince
            }
        };
        this.onlineHandler = () => { if (!this.connected) this.retryConnection(true); this.safeSync(); };
        this.offlineHandler = () => { this.closeSocketOnly(); }; // don’t nuke listeners

        document.addEventListener("visibilitychange", this.visibilityHandler);
        window.addEventListener("online", this.onlineHandler);
        window.addEventListener("offline", this.offlineHandler);
    }

    onFocus(socket: SocketServiceV2) {
        return () => {
            if (!socket.connected) {
                socket.retryConnection();
            }
        }
    }

    connect() {
        isDevelopment() && console.log("Attempting to connect to WebSocket...");
        this.socket = new WebSocket(`${this.socketUrl}?sid=${this.socketIdentifier}`);

        this.socket.onopen = () => {
            isDevelopment() && console.log('Connected to WebSocket');
            this.connected = true;
            this.retryCount = 0;
            this.onConnected();

            if (this.getCursor) {
                const last = this.getCursor();
                if (last) {
                    this.sendMessage('RESUME', { last, userId: this.userId });
                }
            }

            this.startHeartbeat();
        }
        this.socket.onclose = () => {
            isDevelopment() && console.log('Disconnected from WebSocket');
            this.connected = false;
            this.stopHeartbeat();
            this.retryConnection();
        }
        this.socket.onerror = (err: any) => {
            isDevelopment() && console.log('Error from WebSocket: ', err);
            this.connected = false;
        }
        this.socket.onmessage = (event: MessageEvent) => {
            isDevelopment() && console.log('Got WebSocket message');
            const message = JSON.parse(event.data);
            isDevelopment() && console.log('message: ', message);
            this.bumpWatchdog();
            if (message.type === SOCKET_MESSAGE_TYPES.PONG) {
                this.ping = false;
            } else {
                this.callback?.(message);
            }
        };
    }

    // ---------- Heartbeat / watchdog ----------
    private startHeartbeat() {
        this.stopHeartbeat(); // ✅ FIX: clear any previous timers

        // send PING periodically
        this.heartbeatTimer = setInterval(() => {
            if (!this.connected) return;
            this.ping = true;
            isDevelopment() && console.log("socket heartbeat -> PING");
            this.sendMessage(SOCKET_MESSAGE_TYPES.PING, {});

            // if no inbound (incl. PONG) within timeout → reconnect
            this.startWatchdog();
        }, 25000); // ~25s

        // first watchdog kick immediately
        this.startWatchdog();
    }

    private startWatchdog() {
        if (this.pingCheckTimer) clearTimeout(this.pingCheckTimer);
        this.pingCheckTimer = setTimeout(() => {
            if (!this.connected) return;
            if (this.ping) {
                isDevelopment() && console.log("heartbeat watchdog: no PONG -> reconnecting");
                this.closeSocketOnly();
                this.retryConnection();
            }
        }, 10000); // 10s after PING without activity ⇒ stale
    }

    private bumpWatchdog() {
        // any inbound msg implies link is alive
        this.ping = false;
        if (this.pingCheckTimer) {
            clearTimeout(this.pingCheckTimer);
            this.pingCheckTimer = null;
        }
    }

    private stopHeartbeat() {
        if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = null;
        if (this.pingCheckTimer) clearTimeout(this.pingCheckTimer);
        this.pingCheckTimer = null;
        this.ping = false;
    }

    private retryConnection(quick = false) {
        // ✅ FIX: don’t hard-stop after max retries; keep trying with capped backoff
        const delay = quick ? 200 : Math.min(this.baseBackoff * Math.pow(2, this.retryCount++), this.maxBackoff) + Math.floor(Math.random() * 400);

        setTimeout(() => {
            isDevelopment() && console.log(`Reconnection attempt ${this.retryCount} after ${delay}ms...`);
            this.connect();
        }, delay);
    }

    sendMessage(type: string, payload: any): void {
        if (this.socket?.readyState === WebSocket.OPEN) {
            this.socket.send(JSON.stringify({ type, payload }));
        }
    }

    private closeSocketOnly(): void {
        if (this.socket) {
            this.socket.onopen = null;
            this.socket.onclose = null;
            this.socket.onerror = null;
            this.socket.onmessage = null;
            try { this.socket.close(); } catch {}
            this.socket = null;
        }
        this.connected = false;
        this.ping = false;
        this.stopHeartbeat();
    }

    // Keep your public close() to fully tear down the service
    close(): void {
        this.closeSocketOnly();
        // ✅ FIX: remove the exact same listener reference we added
        if (this.focusHandler) window.removeEventListener("focus", this.focusHandler);
        if (this.visibilityHandler) document.removeEventListener("visibilitychange", this.visibilityHandler);
        if (this.onlineHandler) window.removeEventListener("online", this.onlineHandler);
        if (this.offlineHandler) window.removeEventListener("offline", this.offlineHandler);
    }

    setCallback(newCallback: any) {
        this.callback = newCallback;
    }

    public async safeSync() {
        if (!this.syncSince || !this.getCursor) return;
        try {
            const since = this.getCursor() || '';
            if (since) await this.syncSince(since);
        } catch (e) {
            isDevelopment() && console.log('safeSync error (ignored):', e);
        }
    }
}

export default SocketServiceV2;
